import {
  ProductResponse,
  ProductWithPrices,
  Price,
} from '../../../shared/models/pricing/ProductResponse';
import { DiscountResponseData } from '../../../shared/models/pricing/DiscountResponse';
import { t } from 'i18next';
import { AccountType } from '../../../shared/types/account/AccountType';
import { AccountResponse } from '../../../shared/models/account/AccountResponse';

export const ENTERPRISE_LINK =
  'https://calendly.com/sylwia_chatbeat/intro?utm_source=website&utm_medium=cta&utm_campaign=pricing_enterprise&utm_content=get_started_enterprise_button';

export const getPriceWithDiscount = (
  products: ProductWithPrices[],
  slug: string,
  discount: DiscountResponseData,
  billingCycle: string = 'month'
): string => {
  const isPercentage = discount.type === 'percentage';
  const product = products.find((product) => product.custom_data.slug === slug);

  const price = getPriceForBillingCycle(product?.prices, billingCycle)?.unit_price.amount || 0;

  if (!price || price === 0) {
    return `$0`;
  }

  const finalPrice = price / 100;
  if (!discount || Number(discount.amount) === 0) {
    return `$${finalPrice}`;
  }

  if (isPercentage) {
    const discountAmount = (100 - Number(discount.amount)) / 100;
    const priceWithDiscount = parseFloat((finalPrice * discountAmount).toString()).toFixed(0);
    return `$${priceWithDiscount}`;
  }

  return `$${finalPrice}`;
};

export const getPrice = (
  slug: string,
  products: ProductResponse,
  billingCycle: string = 'month'
): { priceId: string; price: string } => {
  const customPlan = {
    priceId: '',
    price: t('custom'),
  };

  if (!products) {
    return customPlan;
  }

  const product = products.data.find((productWithPrices) => productWithPrices.custom_data.slug === slug);
  if (!product || !product.prices || product.prices.length === 0) {
    return customPlan;
  }

  const firstPrice = getPriceForBillingCycle(product?.prices, billingCycle);
  const price = firstPrice?.unit_price;
  if (!price || !price.amount) {
    return customPlan;
  }

  const priceInDollar = price.amount / 100;
  return {
    priceId: firstPrice.id,
    price: `$${priceInDollar}`,
  };
};

export const getPriceForBillingCycle = (
  prices: Price[] | undefined,
  billingCycle: string
): Price | null => {
  if (!prices) {
    return null;
  }

  return prices.find((price) => price.billing_cycle?.interval === billingCycle) || null;
};

const plansDisabledMapV1 = new Map([
  ['TRIAL', ['TRIAL']],
  ['STANDARD', ['TRIAL', 'STANDARD']],
  ['PRO', ['TRIAL', 'PRO']],
  ['ADVANCED', ['TRIAL', 'ADVANCED']],
  ['ENTERPRISE', ['TRIAL', 'STANDARD', 'PRO', 'ADVANCED', 'ENTERPRISE']],
]);

const plansDisabledMapV2 = new Map([
  ['TRIAL', ['TRIAL']],
  ['ESSENTIAL', ['TRIAL', 'ESSENTIAL']],
  ['ADVANCED_V2', ['TRIAL', 'ADVANCED_V2']],
  ['BUSINESS', ['TRIAL', 'BUSINESS']],
  ['ENTERPRISE', ['TRIAL', 'ESSENTIAL', 'ADVANCED_V2', 'BUSINESS', 'ENTERPRISE']],
]);

const plansDisabledMapV3 = new Map([
  ['TRIAL', ['TRIAL']],
  ['ESSENTIAL_V3', ['TRIAL', 'ESSENTIAL_V3']],
  ['ADVANCED_V3', ['TRIAL', 'ADVANCED_V3']],
  ['BUSINESS_V3', ['TRIAL', 'BUSINESS_V3']],
  ['ENTERPRISE_V3', ['TRIAL', 'ESSENTIAL_V3', 'ADVANCED_V3', 'BUSINESS_V3', 'ENTERPRISE_V3']],
]);

export const isDisabledPlan = (
  currentAccountType: AccountType,
  accountTypeToCheck: AccountType,
  productsVersion: string
) => {
  if (
    productsVersion === 'v2' &&
    ['TRIAL', 'STANDARD', 'PRO', 'ADVANCED', 'ENTERPRISE'].includes(currentAccountType)
  ) {
    return false;
  }

  if (
    productsVersion === 'v3' &&
    [
      'TRIAL',
      'STANDARD',
      'PRO',
      'ADVANCED',
      'ADVANCED_V2',
      'ENTERPRISE',
      'ESSENTIAL',
      'BUSINESS',
    ].includes(currentAccountType)
  ) {
    return false;
  }

  let plansDisabledMap;
  if (productsVersion === 'v1') {
    plansDisabledMap = plansDisabledMapV1;
  } else if (productsVersion === 'v2') {
    plansDisabledMap = plansDisabledMapV2;
  } else {
    plansDisabledMap = plansDisabledMapV3;
  }

  const flow = plansDisabledMap.get(currentAccountType);
  return flow && flow.includes(accountTypeToCheck);
};

export const planFeaturesV1 = () => {
  return [
    {
      slug: 'standard',
      name: 'Standard',
      description: t('pricingDescStandard'),
      features: [
        {
          name: `10 ${t('questionsPrompts')}`,
        },
        {
          name: `1 ${t('user').toLowerCase()}`,
        },
      ],
    },
    {
      slug: 'pro',
      name: 'Pro',
      description: t('pricingDescPro'),
      features: [
        {
          name: `30 ${t('questionsPrompts')}`,
        },
        {
          name: `2 ${t('usersText').toLowerCase()}`,
        },
      ],
    },
    {
      slug: 'advanced',
      name: 'Advanced',
      description: t('pricingDescAdvanced'),
      features: [
        {
          name: `100 ${t('questionsPrompts')}`,
        },
        {
          name: `10 ${t('usersText').toLowerCase()}`,
        },
      ],
    },
    {
      slug: 'enterprise',
      name: 'Enterprise',
      description: t('pricingDescEnterprise'),
      features: [
        {
          name: t('unlimitedPrompts'),
        },
        {
          name: t('unlimitedusers'),
        },
        {
          name: t('prioritySupport'),
        },
        {
          name: t('whitelabel'),
        },
      ],
    },
  ];
};

export const planFeaturesV2 = () => {
  return [
    {
      slug: 'essential',
      name: 'Essential',
      description: t('pricingDescEssential'),
      llms: ['GPT', 'CLAUDE', 'GEMINI', 'PERPLEXITY', 'AI-OVERVIEW', 'DEEPSEEK'],
      main: [
        {
          name: t('countPrompts', { count: 30 }).toLowerCase(),
        },
        {
          name: `1 ${t('project').toLowerCase()}`,
        },
        {
          name: `2 ${t('seats').toLowerCase()}`,
        },
      ],
      refreshInfo: t('dailyRefresh', { count: 5400 }),
      features: [
        {
          name: t('customerPromptInsights'),
        },
        {
          name: t('brandVisibilityTracking'),
        },
        {
          name: t('aiSourcesAnalytics'),
        },
        {
          name: t('competitionBenchmarks'),
        },
      ],
    },
    {
      slug: 'advanced_v2',
      name: 'Advanced',
      description: t('pricingDescAdvancedV2'),
      llms: [
        'GPT',
        'CLAUDE',
        'GEMINI',
        'PERPLEXITY',
        'AI-OVERVIEW',
        'COPILOT',
        'GROK',
        'DEEPSEEK',
        'AI-MODE',
      ],
      main: [
        {
          name: t('countPrompts', { count: 100 }).toLowerCase(),
        },
        {
          name: t('unlimitedProjects'),
        },
        {
          name: t('unlimitedSeats'),
        },
      ],
      refreshInfo: t('dailyRefresh', { count: 27000 }),
      features: [
        {
          name: t('customerPromptInsights'),
        },
        {
          name: t('brandVisibilityTracking'),
        },
        {
          name: t('aiSourcesAnalytics'),
        },
        {
          name: t('competitionBenchmarks'),
        },
        {
          name: t('exportableData'),
        },
        // {
        //   name: t('lockerStudioIntegration'),
        // },
      ],
    },
    {
      slug: 'business',
      name: 'Business',
      description: t('pricingDescBusiness'),
      llms: [
        'GPT',
        'CLAUDE',
        'GEMINI',
        'PERPLEXITY',
        'AI-OVERVIEW',
        'COPILOT',
        'GROK',
        'DEEPSEEK',
        'AI-MODE',
      ],
      main: [
        {
          name: t('countPrompts', { count: 300 }).toLowerCase(),
        },
        {
          name: t('unlimitedProjects'),
        },
        {
          name: t('unlimitedSeats'),
        },
      ],
      refreshInfo: t('dailyRefresh', { count: 81000 }),
      features: [
        {
          name: t('customerPromptInsights'),
        },
        {
          name: t('brandVisibilityTracking'),
        },
        {
          name: t('aiSourcesAnalytics'),
        },
        {
          name: t('competitionBenchmarks'),
        },
        {
          name: t('exportableData'),
        },
        // {
        //   name: t('lockerStudioIntegration'),
        // },
        {
          name: t('earlyAccessToBeta'),
        },
        {
          name: t('whitelabeledClientReporting'),
        },
        {
          name: t('dedicatedProjectManger'),
        },
      ],
    },
    {
      slug: 'enterprise',
      name: 'Enterprise',
      description: t('pricingDescEnterpriseV2'),
      llms: [
        'GPT',
        'CLAUDE',
        'GEMINI',
        'PERPLEXITY',
        'AI-OVERVIEW',
        'COPILOT',
        'GROK',
        'DEEPSEEK',
        'AI-MODE',
      ],
      main: [
        {
          name: t('unlimitedPromptsV2'),
        },
        {
          name: t('unlimitedProjects'),
        },
        {
          name: t('unlimitedSeats'),
        },
      ],
      refreshInfo: t('customResponse'),
      features: [
        {
          name: t('customerPromptInsights'),
        },
        {
          name: t('brandVisibilityTracking'),
        },
        {
          name: t('aiSourcesAnalytics'),
        },
        {
          name: t('competitionBenchmarks'),
        },
        {
          name: t('exportableDataApi'),
        },
        // {
        //   name: t('lockerStudioIntegration'),
        // },
        {
          name: t('earlyAccessToBeta'),
        },
        {
          name: t('whitelabeledClientReporting'),
        },
        {
          name: t('dedicatedProjectManger'),
        },
      ],
    },
  ];
};

export const planFeaturesV3 = () => {
  return [
    {
      slug: 'essential_v3',
      name: 'Essential',
      description: t('pricingDescEssential'),
      llms: ['GPT', 'PERPLEXITY', 'AI-OVERVIEW'],
      main: [
        {
          name: t('countPrompts', { count: 30 }).toLowerCase(),
        },
        {
          name: `1 ${t('project').toLowerCase()}`,
        },
        {
          name: `2 ${t('seats').toLowerCase()}`,
        },
      ],
      refreshInfo: t('dailyRefresh', { count: 2700 }),
      features: [
        {
          name: t('customerPromptInsights'),
        },
        {
          name: t('brandVisibilityTracking'),
        },
        {
          name: t('aiSourcesAnalytics'),
        },
        {
          name: t('competitionBenchmarks'),
        },
      ],
    },
    {
      slug: 'advanced_v3',
      name: 'Advanced',
      description: t('pricingDescAdvancedV3'),
      llms: ['GPT', 'GEMINI', 'PERPLEXITY', 'AI-OVERVIEW', 'COPILOT', 'AI-MODE'],
      main: [
        {
          name: t('countPrompts', { count: 100 }).toLowerCase(),
        },
        {
          name: t('unlimitedProjects'),
        },
        {
          name: t('unlimitedSeats'),
        },
      ],
      refreshInfo: t('dailyRefresh', { count: 18000 }),
      features: [
        {
          name: t('customerPromptInsights'),
        },
        {
          name: t('brandVisibilityTracking'),
        },
        {
          name: t('aiSourcesAnalytics'),
        },
        {
          name: t('competitionBenchmarks'),
        },
        {
          name: t('exportableData'),
        },
      ],
    },
    {
      slug: 'business_v3',
      name: 'Business',
      description: t('pricingDescBusinessV3'),
      llms: ['GPT', 'GEMINI', 'PERPLEXITY', 'AI-OVERVIEW', 'COPILOT', 'AI-MODE'],
      main: [
        {
          name: t('countPrompts', { count: 300 }).toLowerCase(),
        },
        {
          name: t('unlimitedProjects'),
        },
        {
          name: t('unlimitedSeats'),
        },
      ],
      refreshInfo: t('dailyRefresh', { count: 54000 }),
      features: [
        {
          name: t('customerPromptInsights'),
        },
        {
          name: t('brandVisibilityTracking'),
        },
        {
          name: t('aiSourcesAnalytics'),
        },
        {
          name: t('competitionBenchmarks'),
        },
        {
          name: t('exportableData'),
        },
        {
          name: t('earlyAccessToBeta'),
        },
        {
          name: t('whitelabeledClientReporting'),
        },
        {
          name: t('dedicatedProjectManger'),
        },
      ],
    },
    {
      slug: 'enterprise_v3',
      name: 'Enterprise',
      description: t('pricingDescEnterpriseV3'),
      llms: [
        'GPT',
        'CLAUDE',
        'GEMINI',
        'PERPLEXITY',
        'AI-OVERVIEW',
        'COPILOT',
        'GROK',
        'DEEPSEEK',
        'AI-MODE',
      ],
      main: [
        {
          name: t('unlimitedPromptsV2'),
        },
        {
          name: t('unlimitedProjects'),
        },
        {
          name: t('unlimitedSeats'),
        },
      ],
      refreshInfo: t('customResponse'),
      features: [
        {
          name: t('customerPromptInsights'),
        },
        {
          name: t('brandVisibilityTracking'),
        },
        {
          name: t('aiSourcesAnalytics'),
        },
        {
          name: t('competitionBenchmarks'),
        },
        {
          name: t('exportableDataApi'),
        },
        {
          name: t('earlyAccessToBeta'),
        },
        {
          name: t('whitelabeledClientReporting'),
        },
        {
          name: t('dedicatedProjectManger'),
        },
      ],
    },
  ];
};

export const getPlanLimit = (plan: string): { maxMonitors: number; maxUsers: number } => {
  switch (plan.trim().toUpperCase()) {
    case 'STANDARD':
      return {
        maxMonitors: 10,
        maxUsers: 1,
      };

    case 'PRO':
      return {
        maxMonitors: 30,
        maxUsers: 2,
      };

    case 'ADVANCED':
      return {
        maxMonitors: 100,
        maxUsers: 10,
      };

    default:
      return {
        maxMonitors: 100000,
        maxUsers: 100000,
      };
  }
};

export const getPlanLimitV2 = (plan: string): { maxMonitors: number; maxUsers: number } => {
  switch (plan.trim().toUpperCase()) {
    case 'ESSENTIAL':
      return {
        maxMonitors: 30,
        maxUsers: 2,
      };

    case 'ADVANCED_V2':
      return {
        maxMonitors: 100,
        maxUsers: 999,
      };

    case 'BUSINESS':
      return {
        maxMonitors: 300,
        maxUsers: 999,
      };

    default:
      return {
        maxMonitors: 100000,
        maxUsers: 100000,
      };
  }
};

export const getPlanLimitV3 = (plan: string): { maxMonitors: number; maxUsers: number } => {
  switch (plan.trim().toUpperCase()) {
    case 'ESSENTIAL_V3':
      return {
        maxMonitors: 30,
        maxUsers: 2,
      };

    case 'ADVANCED_V3':
      return {
        maxMonitors: 100,
        maxUsers: 999,
      };

    case 'BUSINESS_V3':
      return {
        maxMonitors: 300,
        maxUsers: 999,
      };

    default:
      return {
        maxMonitors: 100000,
        maxUsers: 100000,
      };
  }
};

const plansDowngradedMap = new Map([
  ['PRO', ['STANDARD', 'ESSENTIAL']],
  ['ADVANCED', ['STANDARD', 'PRO', 'ESSENTIAL']],
  ['ADVANCED_V2', ['STANDARD', 'PRO', 'ESSENTIAL']],
  ['BUSINESS', ['STANDARD', 'PRO', 'ESSENTIAL', 'ADVANCED', 'ADVANCED_V2']],
  ['ENTERPRISE', ['STANDARD', 'PRO', 'ESSENTIAL', 'ADVANCED', 'ADVANCED_V2']],
]);

export const isDowngrade = (accountResponse: AccountResponse, newPlan: AccountType): boolean => {
  const currentPlan = accountResponse.accountType;
  if (currentPlan === 'TRIAL') {
    return false;
  }

  const downgradedPlans = plansDowngradedMap.get(newPlan);
  return !!downgradedPlans;
};
